import { EndReasonMap, PricingPeriod, ProductType, SubsAction } from '../../../redux/types';
import { dateStrToDate, dateStrToLocaleDate, diffDays } from '../../../utils';
import { useTranslation } from '../../../hooks/useTranslation';
import { TKeys } from '../../../i18n/en';
import { Button, Card } from 'react-bootstrap';
import React from 'react';

interface BoxProps {
  prodType: ProductType;
  onClick: () => any | undefined | void;
  clickText: string;
  isIncluded: boolean;
  loading: boolean;
}

export const ProductDetail = ({ prodType, isIncluded, onClick, clickText, loading }: BoxProps) => {
  const t = useTranslation();
  // FIXME clean this mess
  const { pricings }: any = prodType;
  const theSub = prodType?.currentSubscription;
  const isPaid = !!pricings && !!Object.keys(pricings).length;
  const isActive = !!theSub?.active;
  const isTrial = prodType.actions?.includes(SubsAction.UPGRADE_TRIAL);
  const canTrial = prodType.actions?.includes(SubsAction.SUBSCRIBE_TRIAL);
  const addPaymentLegacy = prodType.actions?.includes(SubsAction.ADD_PAYMENT_METHOD);
  const mustFix = prodType.actions?.includes(SubsAction.USER_ACTION_REQUIRED);
  const isPremiumSpecial = !isIncluded && !isPaid && isActive;
  const discount = isPaid
    ? (
        ((pricings[PricingPeriod.MONTHLY].price * 12.0 - pricings[PricingPeriod.YEARLY].price) /
          pricings[PricingPeriod.MONTHLY].price) *
        12.0
      ).toFixed(1)
    : 0;
  let remainingTrialDays = 0;
  if (isTrial) {
    const endTrialDate = dateStrToDate(theSub?.trialExpirationDate || '');
    const now = new Date();
    remainingTrialDays = diffDays(now, endTrialDate);
  }
  // dateStrToLocaleDate returns false when there is no date; a placeholder needs a string.
  const onDate = (dateStr?: string | null) => dateStrToLocaleDate(dateStr) || '';

  /* The status line used to be concatenated from English fragments, which no
     other language can reproduce. Each fragment is now a sentence of its own
     carrying its date placeholder, and they are joined rather than glued. */
  let head = '';
  const details: string[] = [];
  if (isPaid && isActive) {
    head = t('sub_status_active');
    if (isTrial) {
      details.push(
        t('sub_status_trial_ends', {
          date: onDate(theSub?.trialExpirationDate),
          days: remainingTrialDays,
        })
      );
    }
    if (theSub?.nextBillingDate && !mustFix && !isTrial && !addPaymentLegacy) {
      details.push(t('sub_status_next_billing', { date: onDate(theSub?.nextBillingDate) }));
    }
    if (theSub?.cancellationDate) {
      details.push(
        isTrial
          ? t('sub_status_cancelled')
          : t('sub_status_until_cancelled', { date: onDate(theSub?.endDate) })
      );
    }
    if (mustFix) {
      details.push(t('sub_status_until_payment', { date: onDate(theSub?.endDate) }));
    }
    if (addPaymentLegacy) {
      details.push(
        t('sub_status_until_payment', {
          date: onDate(theSub?.trialExpirationDate),
        })
      );
    }
  } else if (isPaid && !isActive) {
    head = t('sub_status_upgrade');
  } else if (!isPaid && isIncluded) {
    // Plan-agnostic: the active paid plan may be Discovery or Signature.
    head = t('sub_status_included');
  } else if (!isPaid && isActive) {
    head = loading ? t('sub_status_loading') : t('sub_status_active');
  }
  if (isPaid && !isActive && prodType?.lastSubscription?.endDate) {
    const endedKey = EndReasonMap[prodType?.lastSubscription?.endReason || ''];
    if (endedKey) {
      details.push(t(endedKey, { date: onDate(prodType.lastSubscription.endDate) }));
    }
  }
  const subtitle = details.length ? `${head} - ${details.join(' ')}` : head;

  clickText = (() => {
    if (isIncluded && !isPaid) return t('action_included');
    if (isPremiumSpecial && !addPaymentLegacy) return t('action_active');
    if (isPremiumSpecial && addPaymentLegacy) return t('action_add_payment');
    if (isPaid) return clickText;
    return clickText;
  })();

  return (
    <div className="subs-card">
      {/* Rendered even when empty so every plan column starts at the same
          height: only paid/active plans have a status line. */}
      <span className={`subs-card__status${isActive ? ' subs-card__status--active' : ''}`}>
        {subtitle || '\u00a0'}
      </span>
      <Card className={`subs-card__frame${isActive ? ' subs-card__frame--active' : ''}`}>
        <Card.Img className="subs-card__media" variant="top" src={prodType.media} />
        <Card.Body className="subs-card__head">
          <Card.Title>{t(prodType.name)}</Card.Title>
          {clickText && (
            <Button
              className="subs-card__cta"
              variant="primary"
              disabled={!isPaid || isPremiumSpecial}
              onClick={onClick}
            >
              {clickText}
            </Button>
          )}
        </Card.Body>
        <Card.Body className="subs-card__desc">
          {prodType.desc.map((d: TKeys | TKeys[], index: number) =>
            Array.isArray(d) ? (
              <ul key={index} className="subs-card__features">
                {d.map((s: TKeys, i: number) => (
                  <li key={i}>{t(s)}</li>
                ))}
              </ul>
            ) : (
              <p key={index} className="subs-card__line">
                {t(d)}
              </p>
            )
          )}
          {(canTrial || isTrial) && (
            <p className="subs-card__line">
              <b>{t('trial_days', { days: prodType.trialDuration })}</b> {t('trial_offer')}
            </p>
          )}
        </Card.Body>
        {/* Every slot is always rendered, blank when it does not apply, so the
            price lines stay on the same baseline across plans. */}
        <Card.Footer>
          <p className="subs-card__price">
            {!isPaid
              ? t('price_free')
              : t('price_monthly', {
                  price: pricings[PricingPeriod.MONTHLY].price,
                })}
          </p>
          <p className="subs-card__price-year">
            {isPaid ? (
              <>
                <b>
                  {t('price_yearly', {
                    price: pricings[PricingPeriod.YEARLY].price,
                  })}{' '}
                </b>
                <i className="subs-card__save">{t('price_save', { percent: discount })}</i>
              </>
            ) : (
              '\u00a0'
            )}
          </p>
          <hr />
          <p className="subs-card__note">
            {isPaid
              ? t(canTrial || isTrial ? 'subs_cancel_note_trial' : 'subs_cancel_note')
              : '\u00a0'}
          </p>
        </Card.Footer>
      </Card>
    </div>
  );
};
